import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { getMarketingProducts, getPromotionProducts } from '@api/interfaces/productApi';
import RelatedProductsBeam from '@molecules/RelatedProductsBeam/RelatedProductsBeam';
import Spinner from '@molecules/Spinner/Spinner';
import { StyledLoader } from './ProductDetailsRelated.styles';
import type { AxfoodPaginationViewModelAxfoodBasicProductViewModel } from '@occ/api-client';

interface Props {
  product: {
    code: string;
    potentialPromotions?: Array<{ code?: string }>;
  };
  isPage?: boolean;
}

type MarketingVariant = 'same-promotion' | 'others-bought' | 'other-variants' | 'similar-products';

interface VariantResult {
  type: MarketingVariant;
  title: string;
  data: AxfoodPaginationViewModelAxfoodBasicProductViewModel;
}

const ProductDetailsRelated = ({ product, isPage }: Props) => {
  const t = useTranslations('product');
  const [related, setRelated] = useState<VariantResult[] | null>(null);
  const [loading, setLoading] = useState(true);

  const titles: Record<MarketingVariant, string> = {
    'other-variants': t('productDetailsRelated.title.otherVariants'),
    'similar-products': t('productDetailsRelated.title.similarProducts'),
    'others-bought': t('productDetailsRelated.title.othersBought'),
    'same-promotion': t('productDetailsRelated.title.samePromotion'),
  };

  useEffect(() => {
    setRelated(null);
    setLoading(true);

    const controller = new AbortController();
    const { signal } = controller;
    const params = { page: 0, pageSize: 999 };

    const getVariantData = (variant: MarketingVariant) => {
      if (variant === 'same-promotion') {
        const promotion = product.potentialPromotions?.[0]?.code ?? '';
        return promotion ? getPromotionProducts({ ...params, promotion, excludeProducts: product.code }, signal) : null;
      }
      return getMarketingProducts({ ...params, productCode: product.code, variant }, signal);
    };

    const fetch = async () => {
      // The order here is important — only show the first two variants that have data
      const variants: MarketingVariant[] = ['same-promotion', 'others-bought', 'other-variants', 'similar-products'];
      const results: VariantResult[] = [];

      for (const variant of variants) {
        if (results.length === 2) break;
        try {
          const response = await getVariantData(variant);
          if (response?.data?.items?.length) {
            results.push({ type: variant, title: titles[variant], data: response.data });
          }
        } catch {
          // skip failed variants
        }
      }

      // An aborted run (product changed, or unmounted) must not end the loading state of the run that replaced it
      if (signal.aborted) return;

      setRelated(results);
      setLoading(false);
    };

    fetch();

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  if (loading) {
    return (
      <StyledLoader>
        <Spinner size={32} />
      </StyledLoader>
    );
  }

  if (!related?.length) return null;

  return related.map((variant) => (
    <RelatedProductsBeam key={variant.type} title={variant.title} data={variant.data} isPage={isPage} />
  ));
};

export default ProductDetailsRelated;
